import {
  StyledCard,
  StyledInput
} from '@bo-components/common/StyledComponents';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Combobox } from '@library/components/selects';
import type { ComboboxOption } from '@library/components/selects';
import { ConfirmationModal } from '@library/components/modals/ConfirmationModal';
import { DEFAULT_POLL_DATA } from '@bo-constants/polls';
import type { DateTime } from 'luxon';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { FormActionButtons } from '@library/components/buttons';
import { HiddenInput } from '@library/components/inputs';
import PlayerPlaceholder from '@library/assets/images/icons/player-placeholder.svg';
import type { PollData } from '@library/types/polls';
import { PollsDateTimePicker } from '@bo-components/polls/PollsDateTimePicker';
import { PollsImageUploader } from '@bo-components/polls/PollsImageUploader';
import type { SerializedError } from '@reduxjs/toolkit';
import StyledModal from '@bo-components/common/StyledModal';
import { isTimeAfterEST } from '@library/utils/dateTime';
import { useForm } from 'react-hook-form';
import useUtilDetailsModal from '@bo-hooks/utilsDashboards/useUtilDetailsModal';
import { v4 as uuidv4 } from 'uuid';

export interface PollsDetailsModalProps {
  isNewPoll: boolean;
  isOpen: boolean;
  modalData: PollData;
  onClose: () => void;
  onSubmit: (formData: FormData) => void;
  submitMessage?: string;
  error: FetchBaseQueryError | SerializedError | undefined;
  isLoading: boolean;
  isSuccess: boolean;
  readOnly: boolean;
}

const rankOptions = [
  { label: '1', value: '1', isDisabled: false },
  { label: '2', value: '2', isDisabled: false }
];

export const PollsDetailsModal = ({
  isNewPoll,
  isOpen,
  modalData,
  onClose,
  onSubmit,
  submitMessage = '',
  error,
  isLoading,
  isSuccess,
  readOnly
}: PollsDetailsModalProps) => {
  const {
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    trigger
  } = useForm<PollData>({
    mode: 'onChange',
    defaultValues: DEFAULT_POLL_DATA
  });

  const {
    formData,
    handleCancel,
    isReadOnly,
    setFormData,
    setIsReadOnly,
    onClickEdit,
    onClickClose
  } = useUtilDetailsModal({
    isNewItem: isNewPoll,
    defaultItemData: DEFAULT_POLL_DATA,
    modalData,
    onClose,
    reset
  });

  const isEditMode = !isReadOnly || isNewPoll;

  const [imageFile, setImageFile] = useState<File | null>(null);

  const [startTime, setStartTime] = useState<string>(new Date().toISOString());
  const [endTime, setEndTime] = useState<string>(new Date().toISOString());

  const [showConfirmationScreen, setShowConfirmationScreen] = useState(false);

  const [optionOneRank, setOptionOneRank] = useState<
    ComboboxOption | undefined
  >();
  const [optionTwoRank, setOptionTwoRank] = useState<
    ComboboxOption | undefined
  >();

  useEffect(() => {
    if (modalData?.options[0]?.rank) {
      setOptionOneRank({
        label: modalData.options[0].rank,
        value: modalData.options[0].rank,
        isDisabled: false
      });
    }
    if (modalData?.options[1]?.rank) {
      setOptionTwoRank({
        label: modalData.options[1].rank,
        value: modalData.options[1].rank,
        isDisabled: false
      });
    }
  }, [modalData]);

  useEffect(() => {
    if (modalData?.startAt && modalData?.endAt) {
      setStartTime(modalData.startAt);
      setEndTime(modalData.endAt);
    } else {
      setStartTime(new Date().toISOString());
      setEndTime(new Date().toISOString());
    }
  }, [modalData]);

  useEffect(() => {
    if (readOnly) {
      setIsReadOnly(true);
    }
  }, [readOnly, setIsReadOnly]);

  useEffect(() => {
    if (isNewPoll) {
      setIsReadOnly(false);
    }
  }, [isNewPoll, setIsReadOnly]);

  useEffect(() => {
    if (modalData) {
      setFormData(modalData);
    }
  }, [modalData, setFormData]);

  useEffect(() => {
    reset(formData);
  }, [reset, formData]);

  useEffect(() => {
    if (isSuccess) reset();
  }, [reset, isSuccess]);

  const getModalTitle = () => {
    if (isNewPoll) return 'Add Poll';
    if (isEditMode) return 'Edit Poll';
    return 'View Poll';
  };

  const handleImageUpload = (file: File | null) => {
    if (file) {
      setImageFile(file);
    }
  };

  const handleFormSubmit = useCallback(
    (formValues: PollData) => {
      const pollFormData: FormData = new FormData();
      pollFormData.append('poll[question]', formValues.question || '');
      pollFormData.append('poll[description]', formValues.description || '');
      pollFormData.append(
        'poll[banner_description]',
        formValues.bannerDescription || ''
      );
      pollFormData.append(
        'poll[banner_headline]',
        formValues.bannerHeadline || ''
      );
      pollFormData.append('poll[banner_cta]', formValues.bannerCTA || '');
      pollFormData.append('poll[start_at]', formValues.startAt || '');
      pollFormData.append('poll[end_at]', formValues.endAt || '');

      if (imageFile) {
        pollFormData.append('poll[image]', imageFile);
      }

      (formValues.options || []).forEach((option, idx) => {
        pollFormData.append(
          `poll[options_attributes][${idx}][answer]`,
          option.answer || ''
        );
        if (option.id) {
          pollFormData.append(
            `poll[options_attributes][${idx}][id]`,
            option.id || ''
          );
        }
      });

      const optionOneValue = (optionOneRank?.value as string) || '1';
      const optionTwoValue = (optionTwoRank?.value as string) || '2';
      pollFormData.append(`poll[options_attributes][0][rank]`, optionOneValue);
      pollFormData.append(`poll[options_attributes][1][rank]`, optionTwoValue);
      onSubmit(pollFormData);
      setShowConfirmationScreen(false);
    },
    [onSubmit, imageFile, optionOneRank, optionTwoRank]
  );

  const formOptions = useMemo(() => {
    return isNewPoll ? [{ id: uuidv4() }, { id: uuidv4() }] : formData.options;
  }, [isNewPoll, formData]);

  const handleStartDateChange = useCallback(
    async (value: DateTime) => {
      setStartTime(value.toISO() ?? '');
      setValue('startAt', value.toISO() ?? '');
      await trigger();
    },
    [setStartTime, setValue, trigger]
  );

  const handleEndDateChange = useCallback(
    async (value: DateTime) => {
      setEndTime(value.toISO() ?? '');
      setValue('endAt', value.toISO() ?? '');
      await trigger();
    },
    [setEndTime, setValue, trigger]
  );

  const isAfterPollStart = useMemo(() => {
    return (
      formData?.startAt?.length > 0 &&
      isTimeAfterEST(formData?.startAt) &&
      isEditMode
    );
  }, [formData, isEditMode]);

  const handleRankChange = useCallback(
    (selected: ComboboxOption | null, index: number) => {
      const value = selected?.value;
      if (!value) return;
      const valueIndex = value === '1' ? 0 : 1;
      const otherIndex = valueIndex === 0 ? 1 : 0;
      setOptionOneRank(
        index === 0 ? rankOptions[valueIndex] : rankOptions[otherIndex]
      );
      setOptionTwoRank(
        index === 1 ? rankOptions[valueIndex] : rankOptions[otherIndex]
      );
    },
    [setOptionOneRank, setOptionTwoRank]
  );

  return (
    <div>
      {showConfirmationScreen ? (
        <ConfirmationModal
          confirmationCopy="Confirm"
          handleConfirm={handleSubmit(handleFormSubmit)}
          label="Save Poll"
          onCancelModal={() => setShowConfirmationScreen(false)}
          isOpen={isOpen}
        >
          <p>
            This poll has started. <br />
            Are you sure you want to edit this poll?{' '}
          </p>
        </ConfirmationModal>
      ) : (
        <StyledModal
          isEditAllowed={isReadOnly}
          isOpen={isOpen}
          onClose={onClickClose}
          onEdit={onClickEdit}
          testId="poll-details-modal"
          bigTitle={getModalTitle()}
        >
          <form>
            <div className="flex gap-6">
              <StyledCard title="Poll Information">
                <img
                  src={formData.imageUrl || PlayerPlaceholder}
                  alt="Poll"
                  className="border-soFresh-140 h-auto w-32 rounded-md border"
                />
                {isReadOnly && formData?.id && (
                  <StyledInput
                    error={errors.question?.message}
                    label="ID"
                    name="id"
                    readOnly
                    register={register}
                  />
                )}
                <StyledInput
                  error={errors.question?.message}
                  label="Question"
                  name="question"
                  readOnly={isReadOnly}
                  register={register}
                  required
                />
                <StyledInput
                  error={errors.description?.message}
                  label="Description"
                  name="description"
                  readOnly={isReadOnly}
                  register={register}
                  required={false}
                />
                <StyledInput
                  error={errors.bannerDescription?.message}
                  label="Banner Description"
                  name="bannerDescription"
                  readOnly={isReadOnly}
                  register={register}
                  required={false}
                  maxLength={27}
                />
                <StyledInput
                  error={errors.bannerHeadline?.message}
                  label="Banner Headline"
                  name="bannerHeadline"
                  readOnly={isReadOnly}
                  register={register}
                  required={false}
                  maxLength={28}
                />
                <StyledInput
                  error={errors.bannerCTA?.message}
                  label="Banner CTA"
                  name="bannerCTA"
                  readOnly={isReadOnly}
                  register={register}
                  required={false}
                  maxLength={10}
                />
                <div className="flex gap-4">
                  <HiddenInput
                    label="Start Time"
                    name="startAt"
                    register={register}
                    error={errors?.startAt ? 'Start Time is required.' : ''}
                    value={startTime}
                    required
                  >
                    <PollsDateTimePicker
                      handleChange={handleStartDateChange}
                      time={startTime}
                      minDate={new Date()}
                      disabled={isReadOnly}
                    />
                  </HiddenInput>
                  <HiddenInput
                    label="End Time"
                    name="endAt"
                    register={register}
                    error={errors?.endAt ? 'End Time is required.' : ''}
                    value={endTime}
                    required
                  >
                    <PollsDateTimePicker
                      handleChange={handleEndDateChange}
                      time={endTime}
                      minDate={new Date(startTime)}
                      disabled={isReadOnly}
                    />
                  </HiddenInput>
                </div>
                <div>
                  <div className="mb-2 flex text-sm font-medium">
                    Options
                    <p className="text-roses self-start px-1 text-xs">*</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {formOptions.map((option, index: number) => {
                      const { id } = option;
                      const currentValue =
                        index === 0
                          ? (optionOneRank as ComboboxOption)
                          : (optionTwoRank as ComboboxOption);
                      return (
                        <div
                          key={id}
                          className="border-soFresh-130 relative flex flex-col gap-2 rounded border p-4"
                        >
                          <StyledInput
                            label="Answer"
                            name={`options.${index}.answer`}
                            register={register}
                            required
                            readOnly={isReadOnly}
                          />
                          <div className="text-xs">Rank</div>
                          <Combobox
                            isMulti={false}
                            id={`option-${index}`}
                            options={rankOptions}
                            value={currentValue}
                            placeholder=""
                            onChange={(selected: ComboboxOption | null) =>
                              handleRankChange(selected, index)
                            }
                            isClearable={false}
                            disabled={isReadOnly}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
                {!isReadOnly && (
                  <div className="flex flex-col gap-2">
                    <div className="text-sm font-medium">Image URL</div>
                    <PollsImageUploader
                      onImageUpload={handleImageUpload}
                      error={error}
                      isLoading={isLoading}
                      isSuccess={isSuccess}
                    />
                  </div>
                )}
              </StyledCard>
            </div>
            {isEditMode && (
              <div className="border-soFresh-140 bg-soFresh-100 sticky bottom-0 flex items-center justify-end gap-4 border-t border-solid py-3 pr-5">
                {submitMessage ? (
                  <p>{submitMessage}</p>
                ) : (
                  <FormActionButtons
                    className="flex items-center justify-end gap-4 border-none py-3 pr-5"
                    primary={{
                      onClick: isAfterPollStart
                        ? () => setShowConfirmationScreen(true)
                        : handleSubmit(handleFormSubmit),
                      disabled: !isValid
                    }}
                    secondary={{ onClick: handleCancel }}
                  />
                )}
              </div>
            )}
          </form>
        </StyledModal>
      )}
    </div>
  );
};
